import dayjs from 'dayjs';
import 'dayjs/locale/es';
import { FORMATTED_DATES } from '@constants/individualPortal/dashboard/dashboardModelTypes';
import { SSAP_DATE_FORMATS } from '@constants/ssap/miscellaneous/miscellaneous';
import { languageTypes } from '@constants/common/languageTypes';

import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isBetween from 'dayjs/plugin/isBetween';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import objectSupport from 'dayjs/plugin/objectSupport';

export const DATE_FORMAT = 'MM/DD/YYYY';
export const DATE_FORMAT_DASHED = 'YYYY-MM-DD';
export const DATE_FORMAT2 = 'MMM D, YYYY';
export const DATE_FORMAT3 = 'MMMM D, YYYY';
export const DATE_FORMAT4 = 'MMM DD, YYYY';
const DATE_MMM_DD_YYYY_HH_MM = 'MMM DD, YYYY HH:mm';
export const MMM_DD_YYY = 'MMM DD YYYY';
export const US_DATE_TIME_FORMAT_SLASH_LOWERCASE = 'MM/dd/YYYY HH:mm';
export const US_DATE_TIME_FORMAT_SLASH_UPPERCASE = 'MM/DD/YYYY HH:mm';
export const DATE_TIME_FORMAT = 'MMM D, YYYY, h:mm:ss A';

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(isBetween);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(localizedFormat);
dayjs.extend(objectSupport);

export const M_D_YYYY_H_MM_A = 'M/D/YYYY h:mm A';
export const MMM_DD_YYYY_HH_MM = 'MMM dd, yyyy HH:mm';
export const HH_MM = 'HH:mm';
export const MM_DD_YYYY_H_MM_SS = 'MM-DD-YYYY HH:mm:ss';

export const TIME_INTERVALS = {
    SIXTY_DAYS: 60,
    NINETY_DAYS: 90,
    ONE_YEAR: 'ONE_YEAR',
    TWENTY_FOUR_MONTHS: 24,
};

const ONE_DAY_IN_MILLISECONDS = 24 * 60 * 60 * 1000;

const formatDate = date => dayjs(dayjs(date), DATE_FORMAT).startOf('day');

const formatDateTime = date => dayjs(dayjs(date), DATE_FORMAT);

const getDateFormatted = (date, format = DATE_FORMAT) => dayjs(date).format(format);

const getAge = (dob, refYear) => refYear.diff(dob, 'years');
/**
 * Checks if OE is approaching: serverDate is {amount} days before OE Start Date.
 * @param serverDate
 * @param oepStartDate
 * @param amount - default 30
 * @returns {boolean}
 */
const isOEApproaching = (serverDate, oepStartDate, amount = 30) => {
    const diff = oepStartDate.diff(serverDate, 'days');
    return diff > 0 && diff <= amount;
};

/**
 * Checks if OE End Date Approaching
 * @param serverDate - timestamp
 * @param oepStartDate - timestamp
 * @param oepEndDate - timestamp
 * @returns {boolean}
 */
const isOEEndDateApproaching = (serverDate, oepStartDate, oepEndDate) => {
    return dayjs(serverDate).isBetween(oepStartDate, oepEndDate, null, true);
};

/**
 * Returns true if serverDate is between oepEndDate - 3 and oepEnrollEndDate. Inclusively.
 *
 * @returns {boolean}
 * @param serverDate
 * @param oepEndDate
 * @param oepEnrollEndDate
 */
const isExtendedOEPDates = (serverDate, oepEndDate, oepEnrollEndDate) => {
    const oepEndDateRange = oepEndDate.clone().subtract(3, 'days');
    return dayjs(serverDate).isBetween(oepEndDateRange, oepEnrollEndDate, undefined, true);
};

/**
 * Returns upcoming year for renewals.
 * @param serverDate
 * @param oepStartDate
 * @returns {number}
 */
const getUpcomingYearForRenewals = (serverDate, oepStartDate) => {
    const serverDateYear = dayjs(serverDate).year();
    const oepStartDateYear = dayjs(oepStartDate).year();

    return serverDateYear > oepStartDateYear ? serverDateYear : serverDateYear + 1;
};

const getFormattedDate = (type, date, isInsideOEWindow) => {
    switch (type) {
        case FORMATTED_DATES.serverDate:
        case FORMATTED_DATES.oepStartDate:
        case FORMATTED_DATES.exceptionalCircumstancesEndDate:
        case FORMATTED_DATES.oepEndDate:
        case FORMATTED_DATES.globalConfigCoverageStartDate:
        case SSAP_DATE_FORMATS.HRA_START_DATE:
        case FORMATTED_DATES.oepEnrollEndDate: {
            return date.format(DATE_FORMAT);
        }
        case FORMATTED_DATES.currentYear: {
            return parseInt(date);
        }
        case FORMATTED_DATES.previousYear: {
            return parseInt(date) - 1;
        }
        case FORMATTED_DATES.nextYear: {
            return isInsideOEWindow ? parseInt(date) : parseInt(date) + 1;
        }

        case FORMATTED_DATES.oeYear: {
            return dayjs(date).year();
        }

        case FORMATTED_DATES.commentMonthHour: {
            return dayjs(date).format('MMM DD   hh:mma');
        }
        case FORMATTED_DATES.commonDate: {
            return dayjs(date).utc().format(DATE_FORMAT);
        }
        case FORMATTED_DATES.exceptionalCircumstancesEndDateMMMMDo:
            return date.format('MMMM Do');
        case FORMATTED_DATES.exceptionalCircumstancesEndDateESFormat:
            return date.format('MM-DD-YYYY');
    }
};

const getYear = dateToBeProcessed => {
    let processedDate = new Date(dateToBeProcessed);
    return dayjs.utc(processedDate).year();
};



/**
 * Returns month name from date
 * @param dateToBeProcessed
 * @param {string} [language=languageTypes.ENGLISH]
 * @return {string}
 *
 * sample input: 2020-12-31T23:59:59Z / '2024-01-15'
 * getMonthByName('2024-01-15')  // Returns: "January"
 * getMonthByName('2024-01-15', 'es')  // Returns: "Enero"
 * sample output: January
 */

const capitalizeMonthName = monthName =>
    monthName.charAt(0).toUpperCase() + monthName.slice(1);

const getMonthByName = (dateToBeProcessed, language = languageTypes.ENGLISH) => {
    const processedDate = new Date(dateToBeProcessed);
    const monthName = dayjs.utc(processedDate).locale(language).format('MMMM');

    return language === languageTypes.SPANISH
        ? capitalizeMonthName(monthName)
        : monthName;
};

const convertNumberToMonthName = monthNumber => dayjs().month(monthNumber).format('MMMM');

export const formatDateToMMDDYYYY = d => {
    if (d === null || d === '') return '';
    const date = new Date(d);
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Months are zero-based
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
};

/**
 * Removes date info.
 * Example: "Dec 31, 2020, 12:00:00 AM" => "Dec 31, 2020"
 * @param fullDate
 * @returns {*}
 */
const removeTimeFromDateStr = fullDate => fullDate.split(',').slice(0, 2).join(', ');

const isExceptionalCircumstances = (serverDate, exceptionalCircumstancesStartDate, exceptionalCircumstancesEndDate) => {
    return dayjs(serverDate).isBetween(exceptionalCircumstancesStartDate, exceptionalCircumstancesEndDate, null, true);
};

/**
 * Format Date using IntlDate.
 * Example: "Dec 31, 2020, 12:00:00 AM" => "Dec 31, 2020"
 * @param date - String | Date
 * @param args - { options: object, locale: String }
 * @param options - object - { IntlDate options Reference: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DateTimeFormat }
 * @param locale - String - "en-US" | "es-US" | "fr-CA" Reference: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl#locales_argument
 * @returns {*}
 */
const formatDateusingIntl = (date, { options = {}, locale = 'en-US' } = {}) => {
    const formatDate = typeof date === 'string' ? new Date(date) : date;

    const dateOptions = {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        ...options,
    };
    return new Intl.DateTimeFormat(locale, dateOptions).format(formatDate);
};

export const formatToLocale = (date, language, format = DATE_TIME_FORMAT) =>
    dayjs(date).locale(language).format(format);

export const dateMethods = {
    convertNumberToMonthName,
    formatDate,
    formatDateTime,
    getAge,
    getFormattedDate,
    getMonthByName,
    getUpcomingYearForRenewals,
    getYear,
    isExceptionalCircumstances,
    isExtendedOEPDates,
    isOEApproaching,
    isOEEndDateApproaching,
    removeTimeFromDateStr,
    formatDateusingIntl,
    formatDateToMMDDYYYY,
    getDateFormatted,
};

export const DATE_FORMATS = {
    DATE_FORMAT,
    DATE_FORMAT2,
    DATE_FORMAT4,
    DATE_MMM_DD_YYYY_HH_MM,
    DATE_TIME_FORMAT,
    US_DATE_TIME_FORMAT_SLASH_LOWERCASE,
    US_DATE_TIME_FORMAT_SLASH_UPPERCASE,
    M_D_YYYY_H_MM_A,
    MMM_DD_YYYY_HH_MM,
    HH_MM,
    MMM_DD_YYY,
    MM_DD_YYYY_H_MM_SS,
};

// Common server timestamp input formats for parsing
export const SERVER_TIMESTAMP_INPUT_FORMATS = [
    'MM/DD/YYYY HH:mm',
    'M/D/YYYY H:mm',
    'MM/DD/YYYY H:mm',
    'M/D/YYYY HH:mm',
    'YYYY-MM-DD HH:mm:ss',
];

/**
 * Adjusts a UTC date string to the local time zone.
 * Example: "2021-08-24T14:00:00Z", -21600000, 3600000 => { sDate: 'August 24, 2021', sTime: '09:00:00 AM', sDateTime: 'August 24, 2021 at 09:00:00 AM' }
 * @param {string} utcString - The UTC date string to adjust.
 * @param {number} timezoneOffsetMs - The time zone offset in milliseconds.
 * @param {number} dstSavingsMs - The daylight savings time offset in milliseconds.
 * @param {object} options - Additional options for formatting the date. Reference: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DateTimeFormat/DateTimeFormat#locale_options
 * @returns {object} An object containing the adjusted date and time strings.
 */
export const adjustUTCToLocalTime = (utcString, timezoneOffsetMs, dstSavingsMs, options = {}) => {
    const dateOptions = {
        year: 'numeric',
        month: 'long',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true,
        timeZone: 'UTC', // Force UTC time zone for consistency,
        ...options,
    };

    // Parse the UTC time from the input string
    const utcDate = new Date(utcString);

    // Get the UTC timestamp in milliseconds
    const utcTimestamp = utcDate.getTime();

    // Calculate the adjusted timestamp
    const adjustedTimestamp = utcTimestamp + timezoneOffsetMs + dstSavingsMs;

    // Create a new Date object with the adjusted timestamp
    const adjustedDate = new Date(adjustedTimestamp);

    // Format the date part
    const sDate = new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'long',
        day: '2-digit',
        timeZone: 'UTC', // Force UTC time zone for consistency
    }).format(adjustedDate);

    // Format the time part
    const sTime = new Intl.DateTimeFormat('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true,
        timeZone: 'UTC', // Force UTC time zone for consistency
    }).format(adjustedDate);

    const sDateTime = new Intl.DateTimeFormat('en-US', dateOptions).format(adjustedDate);

    return { sDate, sTime, sDateTime };
};

/**
 * Returns the name of the month corresponding to the given month number.
 *
 * @param {number} monthNumber - The month number (1 for January, 2 for February, etc.).
 * @returns {string} The name of the month, or empty string if month number is invalid.
 */
export function getMonthName(monthNumber) {
    const months = [
        'January',
        'February',
        'March',
        'April',
        'May',
        'June',
        'July',
        'August',
        'September',
        'October',
        'November',
        'December',
    ];

    if (monthNumber < 1 || monthNumber > 12) {
        return '';
    }

    return months[monthNumber - 1];
}

/**
 * Formats a given ISO date string into a specified format.
 *
 * @param {string} isoString - The ISO date string to format.
 * @param {string} format - The format string to apply.
 * @returns {string} The formatted date string.
 */
export function formatDateString(isoString, format = 'MM/DD/YYYY') {
    return dayjs(isoString).format(format);
}

/*
 * Get Number of Days between two dates.
 * Example: "2021-08-24T14:00:00Z", "2021-08-25T14:00:00Z" => 1
 * @param {string | Date} date1 - The UTC date string to adjust.
 * @param {string | Date} date2 - The time zone offset in milliseconds.
 * @returns {number} An object containing the adjusted date and time strings.
 */
export const getDifferenceInDays = (date1, date2) => {
    const firstDate = new Date(date1);
    const secondDate = new Date(date2);

    return Math.round(Math.abs((firstDate - secondDate) / ONE_DAY_IN_MILLISECONDS));
};

/**
 * Subtracts a specified number of days from a given date.
 */
export const subtractDays = (date, days) => dayjs(date).subtract(days, 'days').toDate();

/**
 * Subtracts a specified number of months from a given date.
 */
export const subtractMonths = (date, months) => dayjs(date).subtract(months, 'months').toDate();

/**
 * Gets a current date (local browser time).
 * Note: For server time, use getCurrentServerDate or useCurrentServerDate hook.
 */
export const getCurrentDate = () => dayjs().toDate();

/**
 * Creates a Date object representing the current server time that displays correctly in the browser.
 * This function calculates the current server time based on:
 * - The original server UTC time when the page loaded
 * - Time elapsed since page load
 * - Server's timezone offset
 * 
 * Use this function when you need to display/compare against "now" in server time context.
 * 
 * @param {string} serverDateInUTC - The server's date-time in UTC when page loaded (from localDates).
 * @param {number} timezoneOffsetMs - The server timezone offset in milliseconds.
 * @param {number} dstSavingsMs - The DST adjustment in milliseconds.
 * @returns {Date} A Date object that displays as current server time.
 */
export const getCurrentServerDate = (serverDateInUTC, timezoneOffsetMs, dstSavingsMs) => {
    if (!serverDateInUTC) return new Date();
    
    const originalServerTime = new Date(serverDateInUTC);
    const now = new Date();
    
    // Calculate elapsed time since page load
    const elapsedMs = now.getTime() - originalServerTime.getTime();
    
    // Create current server UTC time
    const currentServerUTC = new Date(originalServerTime.getTime() + elapsedMs);
    
    // Convert to display format
    return convertUtcDateToTimezone(currentServerUTC.toISOString(), timezoneOffsetMs, dstSavingsMs);
};

/**
 * Gets a start of the month for a given date.
 */
export const getStartOfMonth = date => dayjs(date).startOf('month').toDate();

/**
 * Gets an end of the month for a given date.
 */
export const getEndOfMonth = date => dayjs(date).endOf('month').toDate();

/**
 * Gets a start of the day for a given date.
 */
export const getStartOfDay = date => dayjs(date).startOf('day').toDate();

/**
 * Gets an end of the day for a given date.
 */
export const getEndOfDay = date => dayjs(date).endOf('day').toDate();

/**
 *
 * @param value the value you want to check is a date
 * @returns true if value if date else false
 */
export function isValidDate(value) {
    const parsedDate = new Date(value);
    return !isNaN(parsedDate.getTime());
}

/**
 * Converts a UTC date string to a Date object that displays as server time in the browser.
 * 
 * This function creates a "fake local" Date where the displayed time values (when rendered
 * by a DatePicker or other component) will match the server's local time, regardless of
 * the user's browser timezone.
 * 
 * Example: Server is EST (UTC-5), user is in PST (UTC-8), server time is 12:30 PM EST
 * - serverDateInUTC = "2025-12-17T17:30:00Z" (12:30 PM EST in UTC)
 * - This function returns a Date that displays as "12:30 PM" in the user's PST browser
 * 
 * @param serverDateInUTC - The UTC date-time string (e.g., "2025-03-05T12:00:00Z").
 * @param timezoneOffsetMs - The server timezone offset in milliseconds (e.g., -18000000 for UTC-5).
 * @param dstSavingsMs - The daylight saving time adjustment in milliseconds (e.g., 3600000 for +1 hour).
 * @returns A JavaScript Date object that displays as server time when rendered locally.
 */
export const convertUtcDateToTimezone = (serverDateInUTC, timezoneOffsetMs, dstSavingsMs) => {
    // Parse the UTC date
    const utcDate = dayjs.utc(serverDateInUTC);
    
    // Add server's timezone offset to get server local time components
    const serverLocalTime = utcDate.add(timezoneOffsetMs + dstSavingsMs, 'millisecond');
    
    // Create a Date object using the server time components as if they were local time
    // This makes the DatePicker display these values regardless of browser timezone
    return new Date(
        serverLocalTime.year(),
        serverLocalTime.month(),
        serverLocalTime.date(),
        serverLocalTime.hour(),
        serverLocalTime.minute(),
        serverLocalTime.second(),
        serverLocalTime.millisecond()
    );
};

/**
 * Creates a Date object from a given date string and format without considering the local timezone.
 *
 * @param {string} dateString - The date string to parse (e.g., "2025-03-05").
 * @param {string} format - The format of the input date string (e.g., "YYYY-MM-DD").
 * @returns {Date} A JavaScript Date object in UTC.
 */
export const createDateWithoutTimezone = (dateString, format = DATE_FORMAT) => {
    const dayjsDate = dayjs(dateString, format); // Parse as a local date
    return new Date(
        dayjsDate.year(),
        dayjsDate.month(),
        dayjsDate.date(),
        dayjsDate.hour(),
        dayjsDate.minute(),
        dayjsDate.second(),
        dayjsDate.millisecond()
    );
};

/**
 * Converts a server timestamp string to the user's local time display format.
 * Server timestamps are stored in server timezone, this converts to local time for display.
 * 
 * The conversion process:
 * 1. Parse the timestamp to extract time components (these represent server local time)
 * 2. Create a UTC timestamp by treating components as server time and subtracting server offset
 * 3. Format the result in the user's browser local timezone
 * 
 * Example: Server is EST (UTC-5), User is PST (UTC-8)
 * - Server timestamp: "12/16/2025 10:30" (10:30 AM EST)
 * - UTC equivalent: 15:30 UTC (10:30 + 5 hours)
 * - Displayed to PST user: 7:30 AM PST (15:30 - 8 hours)
 * 
 * @param {string} serverTimestamp - The timestamp string from server (e.g., "12/16/2025 10:30" or "MM/DD/YYYY HH:mm").
 * @param {number} timezoneOffsetMs - The server timezone offset in milliseconds (e.g., -18000000 for EST/UTC-5).
 * @param {number} dstSavingsMs - The daylight saving time adjustment in milliseconds (e.g., 3600000 for +1 hour).
 * @param {string} outputFormat - The desired output format (default: M_D_YYYY_H_MM_A).
 * @param {string|string[]} inputFormat - The input format(s) to parse the timestamp (default: SERVER_TIMESTAMP_INPUT_FORMATS).
 * @returns {string} The formatted timestamp in user's local time.
 */
export const convertServerTimestampToLocal = (serverTimestamp, timezoneOffsetMs = 0, dstSavingsMs = 0, outputFormat = M_D_YYYY_H_MM_A, inputFormat = SERVER_TIMESTAMP_INPUT_FORMATS) => {
    if (!serverTimestamp) return '';
    
    // Parse the server timestamp - try common formats
    // Note: dayjs parses this as browser-local time, but we need to treat the components as server time
    const parsedDate = dayjs(serverTimestamp, inputFormat, true);
    
    if (!parsedDate.isValid()) {
        // If parsing fails, return the original string
        return serverTimestamp;
    }
    
    // Extract the time components from the parsed date
    // These components represent server local time, NOT browser local time
    const year = parsedDate.year();
    const month = parsedDate.month(); // 0-indexed
    const day = parsedDate.date();
    const hour = parsedDate.hour();
    const minute = parsedDate.minute();
    const second = parsedDate.second();
    
    // Create a UTC Date using these components as if they were UTC,
    // then adjust by subtracting the server's timezone offset to get actual UTC
    // Server offset is negative for west of UTC (e.g., EST = -18000000 = -5 hours)
    // So actual UTC = server_local_time - offset - dst
    // Example: 10:30 EST = 10:30 - (-5 hours) = 10:30 + 5 hours = 15:30 UTC
    const serverTimeAsUtcMs = Date.UTC(year, month, day, hour, minute, second);
    const actualUtcMs = serverTimeAsUtcMs - timezoneOffsetMs - dstSavingsMs;
    
    // Format in the user's local timezone
    // dayjs will automatically convert UTC timestamp to browser local time
    return dayjs(actualUtcMs).format(outputFormat);
};

/**
 * Converts user's actual local time to server time format for API submission.
 * 
 * This function provides better UX by allowing DatePicker to show the user's local time
 * (which is intuitive), and then converting to server time before sending to the API.
 * 
 * Example:
 * - User is in PST (UTC-8), Server is in EST (UTC-5)
 * - User's local time: 2:00 PM PST
 * - Server timezone offset: -5 hours from UTC (-18000000 ms)
 * - Result: "05:00 PM" (server time in EST)
 * 
 * @param {Date} localDate - The user's local Date object from DatePicker
 * @param {number} serverTimezoneOffsetMs - Server's offset from UTC in milliseconds (e.g., -18000000 for EST)
 * @param {number} serverDstSavingsMs - Server's DST savings in milliseconds (e.g., 3600000 if DST active)
 * @param {string} outputFormat - The desired output format (default: US_DATE_TIME_FORMAT_SLASH_UPPERCASE)
 * @returns {string} The formatted timestamp in server time
 */
export const convertLocalToServerTimestamp = (
    localDate,
    serverTimezoneOffsetMs = 0,
    serverDstSavingsMs = 0,
    outputFormat = US_DATE_TIME_FORMAT_SLASH_UPPERCASE
) => {
    if (!localDate) return '';
    
    // Get the UTC timestamp from the user's local date
    // The Date object internally stores UTC time, getTime() returns milliseconds since Unix epoch in UTC
    const utcMs = localDate.getTime();
    
    // Convert UTC to server time by applying server's timezone offset and DST
    // Server offset is typically negative for US timezones (e.g., EST = UTC-5 = -18000000ms)
    // To get server local time: UTC + offset + DST
    // Note: JavaScript Date offsets are opposite sign (getTimezoneOffset returns positive for behind UTC)
    // But our serverTimezoneOffsetMs follows the same convention as JS (negative for behind UTC like EST)
    const serverTimeMs = utcMs + serverTimezoneOffsetMs + serverDstSavingsMs;
    
    // Create a date object and extract the components as they would appear in server timezone
    // We use UTC methods to avoid browser timezone interference
    const serverDate = new Date(serverTimeMs);
    const year = serverDate.getUTCFullYear();
    const month = serverDate.getUTCMonth();
    const day = serverDate.getUTCDate();
    const hour = serverDate.getUTCHours();
    const minute = serverDate.getUTCMinutes();
    const second = serverDate.getUTCSeconds();
    
    // Format using dayjs with explicit date components
    return dayjs()
        .year(year)
        .month(month)
        .date(day)
        .hour(hour)
        .minute(minute)
        .second(second)
        .format(outputFormat);
};
